import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../contexts/AuthContext';
import { addComment, getComments, deleteComment } from '../../services/comments';
import type { Comment } from '../../types';

interface CommentSectionProps {
  itemId: string;
}

export default function CommentSection({ itemId }: CommentSectionProps) {
  const { user } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);

  const loadComments = async () => {
    const result = await getComments(itemId);
    setComments(result);
  };

  useEffect(() => {
    loadComments();
  }, [itemId]);

  const handlePost = async () => {
    if (!user || !text.trim()) return;
    setPosting(true);
    try {
      await addComment(itemId, user.uid, text.trim());
      setText('');
      await loadComments();
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = async (commentId: string) => {
    await deleteComment(itemId, commentId);
    await loadComments();
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Comments
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Add a comment..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handlePost()}
        />
        <Button variant="contained" onClick={handlePost} disabled={posting || !text.trim()}>
          Post
        </Button>
      </Box>

      <List disablePadding>
        {comments.map((comment) => (
          <ListItem
            key={comment.id}
            disableGutters
            secondaryAction={
              comment.createdBy === user?.uid ? (
                <IconButton size="small" onClick={() => handleDelete(comment.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              ) : undefined
            }
          >
            <ListItemText
              primary={comment.text}
              secondary={comment.createdAt.toLocaleDateString()}
            />
          </ListItem>
        ))}
      </List>

      {comments.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No comments yet.
        </Typography>
      )}
    </Box>
  );
}
